import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from 'recharts';
import { apiFetch } from '../../../../lib/api';
import { usePermesso } from '../../../../context/PermessiContext';
import { formatQuantity } from '../../../../utils/formatters';
import { showToast } from '../../../../components/Toast';
import BopRowActions, { RowIcon } from '../../../../components/BopRowActions';
import { CommessaDettaglio } from '../SchedaCommessa';
import { API_DOCS } from './TabRiepilogo.api';
import ApiInspector from '../../../../components/ApiInspector';

// ── Tipi ─────────────────────────────────────────────────────────────────────

interface OrdineConRighe {
  id: number;
  numero: string;
  importoTotale: number;
  salEvaso: number;
}

interface SalItem {
  id: number;
  numero: string | null; // assegnato all'emissione dal Numeratore
  stato: string;
  importoTotale: number;
  dataEmissione?: string | null;
  dataApprovazione?: string | null;
}

interface Distinta {
  id: number;
  tipo: 'MERCE' | 'LAVORAZIONI';
  stato: 'BOZZA' | 'CONFERMATA';
  _count?: { righe: number };
}

interface FabbisognoMerce {
  chiave: string;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number;
  quantitaEvasa: number;
  residua: number;
  residuoDaOrdinare?: number;
  giacenza: number | null;
}

type StatoOrdineFornitore = 'BOZZA' | 'EMESSO' | 'ANNULLATO';

// Ordine fornitore con almeno una riga di questa commessa (importi sulle sole righe della commessa)
interface OrdineFornitoreCommessa {
  id: number;
  numero: string | null;
  stato: StatoOrdineFornitore;
  dataEmissione: string | null;
  createdAt: string;
  fornitoreRagioneSociale: string | null;
  fornitore: { id: number; ragioneSociale: string | null } | null;
  righeCommessa: number;
  importoCommessa: number;
  importoCommessaCompleto: boolean;
}

// Articolo con residuo da ordinare nelle RDA aperte della commessa
interface ArticoloDaOrdinare {
  chiave: string;
  sorgenti: { rigaRdaId: number }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

const avatarColor = (nome: string) => {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];
  let h = 0;
  for (let i = 0; i < nome.length; i++) h = nome.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
};

const initials = (nome: string) =>
  nome.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

// ── Helper: progress bar fill color class ────────────────────────────────────

function progressFillClass(pct: number, fullIsSuccess?: boolean): string {
  if (fullIsSuccess && pct >= 100) return 'bop-riepilogo__progress-fill bop-riepilogo__fill--success';
  return 'bop-riepilogo__progress-fill bop-riepilogo__fill--primary';
}

function pctColorClass(pct: number): string {
  if (pct >= 80) return 'bop-riepilogo__fill--success';
  if (pct >= 50) return 'bop-riepilogo__fill--warning';
  return 'bop-riepilogo__fill--danger';
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function TabRiepilogo({ commessa, onRefresh }: { commessa: CommessaDettaglio; onRefresh: () => void }) {
  const { can } = usePermesso();
  const canFabbisogni = can('commerciale.fabbisogni', 'view');
  const canOrdiniForn = can('acquisti.ordini_fornitori', 'view');
  const canRda        = can('acquisti.rda', 'view');

  const [ordini,     setOrdini]     = useState<OrdineConRighe[]>([]);
  const [sal,        setSal]        = useState<SalItem[]>([]);
  const [distinte,   setDistinte]   = useState<Distinta[]>([]);
  const [fabbisogni, setFabbisogni] = useState<FabbisognoMerce[]>([]);
  const [loading,    setLoading]    = useState(true);

  // Ordini fornitore della commessa + residuo RDA da ordinare: caricati a parte,
  // così un permesso acquisti mancante o un errore non svuotano il resto del cruscotto
  const [ordiniForn,   setOrdiniForn]   = useState<OrdineFornitoreCommessa[]>([]);
  const [daOrdinare,   setDaOrdinare]   = useState<ArticoloDaOrdinare[]>([]);
  const [loadingForn,  setLoadingForn]  = useState(false);

  const caricaAcquisti = useCallback(async () => {
    if (!canOrdiniForn && !canRda) return;
    setLoadingForn(true);
    const qs = `societaId=${commessa.societaId}&commessaId=${commessa.id}`;
    if (canOrdiniForn) {
      try {
        // @api GET /api/ordini-fornitori?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"id":"number","numero":"string|null","stato":"BOZZA|EMESSO|ANNULLATO","dataEmissione":"string|null","createdAt":"string","fornitoreRagioneSociale":"string|null","fornitore":{"id":"number","ragioneSociale":"string"},"righeCommessa":"number","importoCommessa":"number","importoCommessaCompleto":"boolean"}]}
        const r = await apiFetch<{ success: boolean; data: OrdineFornitoreCommessa[] }>(`/api/ordini-fornitori?${qs}`);
        setOrdiniForn(r.data ?? []);
      } catch {
        setOrdiniForn([]);
      }
    }
    if (canRda) {
      try {
        // @api GET /api/rda/da-ordinare?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"chiave":"string","descrizione":"string","totaleDaOrdinare":"number","sorgenti":[{"rigaRdaId":"number","residuo":"number"}]}]}
        const r = await apiFetch<{ success: boolean; data: ArticoloDaOrdinare[] }>(`/api/rda/da-ordinare?${qs}`);
        setDaOrdinare(r.data ?? []);
      } catch {
        setDaOrdinare([]);
      }
    }
    setLoadingForn(false);
  }, [commessa.id, commessa.societaId, canOrdiniForn, canRda]);

  useEffect(() => { caricaAcquisti(); }, [caricaAcquisti]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [oRes, sRes, dRes, fRes] = await Promise.all([
          // @api GET /api/ordini?commessaId={id} -> {"success":true,"data":[{"id":"number","numero":"string","totale":"string","importoTotale":"string"}]}
          apiFetch<{ success: boolean; data: any[] }>(`/api/ordini?commessaId=${commessa.id}`),
          // @api GET /api/sal?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"id":"number","numero":"string|null","stato":"string","importoTotale":"string","dataEmissione":"string|null","dataApprovazione":"string|null"}]}
          apiFetch<{ success: boolean; data: SalItem[] }>(
            `/api/sal?societaId=${commessa.societaId}&commessaId=${commessa.id}`
          ),
          // @api GET /api/distinte?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"id":"number","numero":"number","tipo":"MERCE|LAVORAZIONI","addebito":"NOSTRO|CLIENTE","stato":"BOZZA|CONFERMATA","_count":{"righe":"number"}}]}
          apiFetch<{ success: boolean; data: Distinta[] }>(
            `/api/distinte?societaId=${commessa.societaId}&commessaId=${commessa.id}`
          ),
          // @api GET /api/fabbisogni/merce?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"chiave":"string","descrizione":"string","unitaMisura":"string","quantita":"number","quantitaEvasa":"number","residua":"number","giacenza":"number|null"}]}
          canFabbisogni
            ? apiFetch<{ success: boolean; data: FabbisognoMerce[] }>(
                `/api/fabbisogni/merce?societaId=${commessa.societaId}&commessaId=${commessa.id}`
              )
            : Promise.resolve({ success: true, data: [] as FabbisognoMerce[] }),
        ]);

        // Calcola SAL evaso per ordine
        const salItems = sRes.data ?? [];
        const ordiniRaw = oRes.data ?? [];

        // Mappa sal approvati per rigaOrdineId (sommiamo importo per ordine)
        // Per semplicità usiamo il totale SAL approvati diviso per numero ordini
        const salApprovati = salItems.filter(s =>
          ['APPROVATO_CLIENTE','APPROVATO_BACKOFFICE','FATTURATO'].includes(s.stato)
        );
        const totaleSalAppr = salApprovati.reduce((sum, s) => sum + Number(s.importoTotale), 0);

        // Distribuzione proporzionale sui ordini.
        // Nota Nodo 3: l'endpoint nuovo /api/ordini espone "totale" (IVA inclusa).
        // Manteniamo fallback su "importoTotale" per compatibilità con vecchi snapshot in cache.
        const valoreOrdine = (o: any) => Number(o.totale ?? o.importoTotale ?? 0);
        const totaleOrdini = ordiniRaw.reduce((sum: number, o: any) => sum + valoreOrdine(o), 0);
        const ordiniConSal = ordiniRaw.map((o: any) => {
          const importo = valoreOrdine(o);
          const peso = totaleOrdini > 0 ? importo / totaleOrdini : 0;
          return { id: o.id, numero: o.numero, importoTotale: importo, salEvaso: totaleSalAppr * peso };
        });

        setOrdini(ordiniConSal);
        setSal(salItems);
        setDistinte(dRes.data ?? []);
        setFabbisogni(fRes.data ?? []);
      } catch {}
      finally { setLoading(false); }
    };
    fetch();
  }, [commessa.id, commessa.societaId, canFabbisogni]);

  const kpi = commessa.kpi;
  const avanzPct = kpi.totaleOrdini > 0
    ? Math.min(100, Math.round((kpi.totaleSal / kpi.totaleOrdini) * 100))
    : 0;
  const deltaBudget = commessa.budgetIniziale != null
    ? kpi.totaleOrdini - Number(commessa.budgetIniziale)
    : null;

  // Distinte per tipo
  const distMerce      = distinte.filter(d => d.tipo === 'MERCE');
  const distLavorazioni = distinte.filter(d => d.tipo === 'LAVORAZIONI');

  // Fabbisogni merce (viste in tempo reale)
  const fabbQtaRichiesta   = fabbisogni.reduce((s, f) => s + Number(f.quantita ?? 0), 0);
  const fabbQtaResidua     = fabbisogni.reduce((s, f) => s + Number(f.residua ?? 0), 0);
  const fabbDaApprovv      = fabbisogni.filter(f => Number(f.giacenza ?? 0) < Number(f.residua)).length;
  const fabbCoperti        = fabbisogni.length - fabbDaApprovv;

  // Dati grafico barre ordini vs SAL
  const barData = ordini.map(o => ({
    name: `N.${o.numero}`,
    Ordine:  parseFloat(o.importoTotale.toFixed(2)),
    'SAL App.': parseFloat(o.salEvaso.toFixed(2)),
    Residuo: parseFloat(Math.max(0, o.importoTotale - o.salEvaso).toFixed(2)),
  }));

  // Dati grafico linea avanzamento temporale
  const salApprovatiSorted = sal
    .filter(s => ['APPROVATO_CLIENTE','APPROVATO_BACKOFFICE','FATTURATO'].includes(s.stato) && s.dataApprovazione)
    .sort((a, b) => new Date(a.dataApprovazione!).getTime() - new Date(b.dataApprovazione!).getTime());

  let cumulo = 0;
  const lineData = salApprovatiSorted.map(s => {
    cumulo += Number(s.importoTotale);
    return { data: fmtDate(s.dataApprovazione), 'Cumulato': cumulo };
  });

  // Ultimi SAL (id decrescente = ordine di creazione)
  const ultimiSal = [...sal].sort((a, b) => b.id - a.id).slice(0, 5);

  // Fabbisogno merce in tempo reale (righe con residuo, max 5)
  const fabbConResiduo   = fabbisogni.filter(f => Number(f.residua) > 0);
  const fabbResiduoItems = fabbConResiduo.slice(0, 5);

  // Ordini fornitore: gli annullati non contano nel valore ordinato
  const ordiniFornVivi   = ordiniForn.filter(o => o.stato !== 'ANNULLATO');
  const ordiniFornEmessi = ordiniForn.filter(o => o.stato === 'EMESSO');
  const valoreOrdinato   = ordiniFornEmessi.reduce((s, o) => s + Number(o.importoCommessa || 0), 0);
  const righeDaOrdinare  = daOrdinare.reduce((s, a) => s + a.sorgenti.length, 0);

  if (loading) return <div className="inline-loading">Caricamento riepilogo…</div>;

  return (
    <div className="bop-riepilogo">
      {/* ── RIGA 1: KPI principali ─────────────────────────────────────────── */}
      <div className="bop-riepilogo__grid-4">
        <KpiCard title="Valore Ordini" primary={fmt(kpi.totaleOrdini)}
          sub={`${kpi.nOrdini} ordine${kpi.nOrdini !== 1 ? 'i' : ''}`} color="var(--bop-color-info)" />
        <KpiCard title="SAL Approvati" primary={fmt(kpi.totaleSal)}
          sub={`${kpi.nSal} SAL`} color="var(--bop-color-primary)" />
        <div className="bop-riepilogo__kpi-box">
          <div className="bop-riepilogo__kpi-label">Avanzamento</div>
          <div className="bop-riepilogo__kpi-value--lg">{avanzPct}%</div>
          <div className="bop-riepilogo__progress">
            {/* width is computed dynamically — inline style allowed per rules */}
            <div className={progressFillClass(avanzPct, true)} style={{ width: `${avanzPct}%` }} />
          </div>
        </div>
        <div className="bop-riepilogo__kpi-box">
          <div className="bop-riepilogo__kpi-label">Budget</div>
          {commessa.budgetIniziale != null ? (
            <>
              <div className="bop-riepilogo__budget-value">{fmt(Number(commessa.budgetIniziale))}</div>
              <div className="bop-riepilogo__budget-vs">vs {fmt(kpi.totaleOrdini)} ordinato</div>
              {deltaBudget !== null && (
                <div className={`bop-riepilogo__budget-delta ${deltaBudget >= 0 ? 'bop-riepilogo__budget-delta--positive' : 'bop-riepilogo__budget-delta--negative'}`}>
                  {deltaBudget >= 0 ? '+' : ''}{fmt(deltaBudget)}
                </div>
              )}
            </>
          ) : (
            <div className="bop-riepilogo__budget-empty">Non impostato</div>
          )}
        </div>
      </div>

      {/* ── RIGA 2: KPI operativi ──────────────────────────────────────────── */}
      <div className="bop-riepilogo__grid-5">
        <div className="bop-riepilogo__kpi-box">
          <div className="bop-riepilogo__kpi-label">Distinte Merce</div>
          <div className="bop-riepilogo__kpi-value">{distMerce.length}</div>
          <div className="bop-riepilogo__distinte-breakdown">
            <span className="bop-riepilogo__distinte-bozza">Bozza: <b>{distMerce.filter(d => d.stato === 'BOZZA').length}</b></span>
            <span className="bop-riepilogo__distinte-approvata">Confermate: <b>{distMerce.filter(d => d.stato === 'CONFERMATA').length}</b></span>
          </div>
        </div>
        <div className="bop-riepilogo__kpi-box">
          <div className="bop-riepilogo__kpi-label">Distinte Lavorazioni</div>
          <div className="bop-riepilogo__kpi-value">{distLavorazioni.length}</div>
          <div className="bop-riepilogo__distinte-breakdown">
            <span className="bop-riepilogo__distinte-bozza">Bozza: <b>{distLavorazioni.filter(d => d.stato === 'BOZZA').length}</b></span>
            <span className="bop-riepilogo__distinte-approvata">Confermate: <b>{distLavorazioni.filter(d => d.stato === 'CONFERMATA').length}</b></span>
          </div>
        </div>
        {canFabbisogni && (
          <div className="bop-riepilogo__kpi-box">
            <div className="bop-riepilogo__kpi-label">Fabbisogni</div>
            <div className="bop-riepilogo__kpi-value--sm">{fabbisogni.length}</div>
            <div className="bop-riepilogo__stat-col">
              <span>Q.tà richiesta: <b>{formatQuantity(fabbQtaRichiesta)}</b></span>
              <span>Q.tà residua: <b>{formatQuantity(fabbQtaResidua)}</b></span>
              <span className={fabbDaApprovv > 0 ? 'bop-riepilogo__fabb-attesa' : 'bop-riepilogo__fabb-attesa--none'}>
                Da approvvigionare: {fabbDaApprovv}
              </span>
            </div>
          </div>
        )}

        <div className="bop-riepilogo__kpi-box">
          <div className="bop-riepilogo__kpi-label">
            Team
          </div>
          <div className="bop-riepilogo__kpi-value">
            {commessa.team.length + 1}
          </div>
          <div className="bop-riepilogo__team-avatars">
            {/* Responsabile + primi 4 membri */}
            {[{ nomeCompleto: commessa.responsabileNome ?? '' }, ...commessa.team].slice(0, 5).map((m, i) => (
              <div key={i} title={(m as any).nomeCompleto}
                className="bop-riepilogo__avatar"
                style={{ background: avatarColor((m as any).nomeCompleto ?? '') }}>
                {initials((m as any).nomeCompleto ?? '?')}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGA 3: Grafici ────────────────────────────────────────────────── */}
      <div className="bop-riepilogo__grid-2">
        <div className="bop-riepilogo__kpi-box--chart">
          <div className="bop-riepilogo__chart-title">Ordini vs SAL</div>
          {barData.length === 0 ? (
            <div className="bop-riepilogo__chart-empty">Nessun ordine</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={barData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v: any) => fmt(Number(v))} />
                <Bar dataKey="Ordine"    fill="#dbeafe" radius={[3,3,0,0]} />
                <Bar dataKey="SAL App."  fill="#3b82f6" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bop-riepilogo__kpi-box--chart">
          <div className="bop-riepilogo__chart-title">Avanzamento Temporale</div>
          {lineData.length === 0 ? (
            <div className="bop-riepilogo__chart-empty">Nessun SAL approvato</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={lineData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v: any) => fmt(Number(v))} />
                <Line type="monotone" dataKey="Cumulato" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── RIGA 4: Tabelle sintetiche ─────────────────────────────────────── */}
      <div className="bop-riepilogo__grid-2--no-mb">
        {/* Ultimi SAL */}
        <div className="bop-riepilogo__kpi-box--table">
          <div className="bop-riepilogo__table-header">
            Ultimi SAL emessi
          </div>
          {ultimiSal.length === 0 ? (
            <div className="bop-riepilogo__table-empty">Nessun SAL</div>
          ) : (
            <table className="bop-riepilogo__table">
              <thead>
                <tr>
                  <th>N.</th>
                  <th>Data</th>
                  <th className="bop-text-right">Importo</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {ultimiSal.map(s => (
                  <tr key={s.id}>
                    <td className="bop-riepilogo__td-mono">{s.numero ?? 'Bozza'}</td>
                    <td className="bop-riepilogo__td-muted">{fmtDate(s.dataEmissione)}</td>
                    <td className="bop-riepilogo__td-amount">{fmt(Number(s.importoTotale))}</td>
                    <td>
                      <SalStatoBadge stato={s.stato} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Fabbisogno merce (tempo reale) */}
        {canFabbisogni && (
          <div className="bop-riepilogo__kpi-box--table">
            <div className="bop-riepilogo__table-header--flex">
              <span>Fabbisogno merce (tempo reale)</span>
              {fabbDaApprovv > 0 && (
                <span className="bop-riepilogo__fabb-alert">{fabbDaApprovv} da approvvigionare</span>
              )}
            </div>
            {fabbResiduoItems.length === 0 ? (
              <div className="bop-riepilogo__table-empty">
                {fabbisogni.length === 0 ? 'Nessun fabbisogno merce' : '✓ Nessuna quantità residua'}
              </div>
            ) : (
              <div className="bop-riepilogo__fabb-list">
                {fabbResiduoItems.map(f => (
                  <div key={f.chiave} className="bop-riepilogo__fabb-item">
                    <div className="bop-riepilogo__fabb-item-desc">{f.descrizione}</div>
                    <div className="bop-riepilogo__fabb-item-qty">
                      Residua: {formatQuantity(f.residua)} {f.unitaMisura ?? ''}
                    </div>
                  </div>
                ))}
                {fabbConResiduo.length > 5 && (
                  <div className="bop-riepilogo__fabb-more">
                    + altre {fabbConResiduo.length - 5} righe con residuo
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── ORDINI FORNITORE ───────────────────────────────────────────────── */}
      {(canOrdiniForn || canRda) && (
        <PannelloOrdiniFornitore
          commessa={commessa}
          ordini={ordiniForn}
          righeDaOrdinare={righeDaOrdinare}
          righeDaMettereInRda={fabbisogni.filter(f => Number(f.residuoDaOrdinare ?? 0) > 0).length}
          loading={loadingForn}
          onRicarica={caricaAcquisti}
        />
      )}

      {/* ── FLUSSI PARALLELI ───────────────────────────────────────────────── */}
      {(() => {
        const salEmessi    = sal.filter(s => s.stato !== 'BOZZA');
        const salApprovati = sal.filter(s => ['APPROVATO_CLIENTE','APPROVATO_BACKOFFICE','FATTURATO'].includes(s.stato));
        const salFatturati = sal.filter(s => s.stato === 'FATTURATO');
        const totEmessi    = salEmessi.reduce((s, x) => s + Number(x.importoTotale), 0);
        const totAppr      = salApprovati.reduce((s, x) => s + Number(x.importoTotale), 0);
        const totFatt      = salFatturati.reduce((s, x) => s + Number(x.importoTotale), 0);
        const residuo      = kpi.totaleOrdini - totFatt;
        const distMerceConf = distinte.filter(d => d.tipo === 'MERCE' && d.stato === 'CONFERMATA').length;
        const pctCopertura = fabbisogni.length > 0 ? Math.round((fabbCoperti / fabbisogni.length) * 100) : 0;
        const margineOk    = residuo >= 0;
        const pctFillClass = pctColorClass(pctCopertura);
        return (
          <div className="bop-riepilogo__flussi">
            <div className="bop-riepilogo__flussi-title">Flussi Paralleli</div>
            <div className="bop-riepilogo__grid-2--no-mb">
              {/* Flusso Costi */}
              {canFabbisogni && (
                <div className="bop-riepilogo__flusso-box bop-riepilogo__flusso-box--costi">
                  <div className="bop-riepilogo__flusso-heading bop-riepilogo__flusso-heading--costi">
                    Flusso Costi — Approvvigionamento
                  </div>
                  <FlussRow label="Distinte merce confermate"   value={String(distMerceConf)} />
                  <FlussRow label="Righe fabbisogno"            value={String(fabbisogni.length)} />
                  <FlussRow label="Di cui coperte da giacenza"  value={String(fabbCoperti)}   ok />
                  <FlussRow label="Da ordinare"                 value={String(fabbDaApprovv)} warn={fabbDaApprovv > 0} />
                  {canOrdiniForn && (
                    <>
                      <FlussRow label="Ordini fornitore emessi" value={`${ordiniFornEmessi.length} di ${ordiniFornVivi.length}`} />
                      <FlussRow label="Valore ordinato (prezzi attesi)" value={fmt(valoreOrdinato)} />
                    </>
                  )}
                  <div className="bop-riepilogo__flusso-footer bop-riepilogo__flusso-footer--costi">
                    <div className="bop-riepilogo__flusso-footer-row">
                      <span className="bop-riepilogo__fluss-row-label">% copertura giacenza</span>
                      <span className={`bop-riepilogo__fluss-row-value${pctCopertura >= 80 ? '--ok' : pctCopertura >= 50 ? '' : '--warn'}`}>
                        {pctCopertura}%
                      </span>
                    </div>
                    <div className="bop-riepilogo__progress--sm">
                      {/* width is computed dynamically */}
                      <div className={`bop-riepilogo__progress-fill--sm ${pctFillClass}`} style={{ width: `${pctCopertura}%` }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Flusso Ricavi */}
              <div className="bop-riepilogo__flusso-box bop-riepilogo__flusso-box--ricavi">
                <div className="bop-riepilogo__flusso-heading bop-riepilogo__flusso-heading--ricavi">
                  Flusso Ricavi — Fatturazione
                </div>
                <FlussRow label="Valore ordini"       value={fmt(kpi.totaleOrdini)} />
                <FlussRow label="SAL emessi"          value={fmt(totEmessi)}         />
                <FlussRow label="SAL approvati"       value={fmt(totAppr)}           ok />
                <FlussRow label="SAL fatturati"       value={fmt(totFatt)}           ok />
                <div className="bop-riepilogo__flusso-footer bop-riepilogo__flusso-footer--ricavi">
                  <div className="bop-riepilogo__flusso-footer-row">
                    <span className="bop-riepilogo__fluss-row-label">Residuo da fatturare</span>
                    <span className={`bop-riepilogo__fluss-row-value${residuo >= 0 ? '--ok' : '--warn'}`}>
                      {fmt(residuo)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bilanciamento */}
            <div className={`bop-riepilogo__balance-banner ${margineOk ? 'bop-riepilogo__balance-banner--ok' : 'bop-riepilogo__balance-banner--warn'}`}>
              <span className="bop-riepilogo__balance-icon">{margineOk ? '✅' : '⚠️'}</span>
              <div className="bop-riepilogo__balance-body">
                <div className={`bop-riepilogo__balance-title ${margineOk ? 'bop-riepilogo__balance-title--ok' : 'bop-riepilogo__balance-title--warn'}`}>
                  {margineOk ? 'Bilanciamento positivo' : 'Attenzione: residuo negativo'}
                </div>
                <div className="bop-riepilogo__balance-desc">
                  {margineOk
                    ? `Residuo da fatturare: ${fmt(residuo)} — il progetto è in linea con il budget.`
                    : `Il totale fatturato supera il valore degli ordini di ${fmt(Math.abs(residuo))}.`}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}

// ── Sub-componenti ────────────────────────────────────────────────────────────

function KpiCard({ title, primary, sub, color }: { title: string; primary: string; sub: string; color: string }) {
  return (
    <div className="bop-riepilogo__kpi-box">
      <div className="bop-riepilogo__kpi-label">{title}</div>
      <div className="bop-riepilogo__kpi-primary">{primary}</div>
      {/* color is a dynamic CSS variable reference, so inline style is needed */}
      <div className="bop-riepilogo__kpi-sub" style={{ color }}>{sub}</div>
    </div>
  );
}

const SAL_STATO_BADGE: Record<string, { label: string; cls: string }> = {
  BOZZA:               { label: 'Bozza',       cls: 'badge-slate'  },
  EMESSO:              { label: 'Emesso',       cls: 'badge-blue'   },
  APPROVATO_CLIENTE:   { label: 'App. Cliente', cls: 'badge-green'  },
  APPROVATO_BACKOFFICE:{ label: 'App. BO',      cls: 'badge-green'  },
  FATTURATO:           { label: 'Fatturato',    cls: 'badge-yellow' },
};

function SalStatoBadge({ stato }: { stato: string }) {
  const cfg = SAL_STATO_BADGE[stato] ?? { label: stato, cls: 'badge-slate' };
  return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
}

const STATO_ORDINE_FORN: Record<StatoOrdineFornitore, { label: string; cls: string }> = {
  BOZZA:     { label: 'Bozza',     cls: 'bop-badge-neutral' },
  EMESSO:    { label: 'Emesso',    cls: 'bop-badge-success' },
  ANNULLATO: { label: 'Annullato', cls: 'bop-badge-danger'  },
};

// Gestione degli ordini fornitore dal cruscotto: l'ordine fornitore non ha la
// commessa in testata, la eredita dalle righe RDA da cui nasce. Da qui si
// avvia la richiesta d'acquisto sul fabbisogno della commessa, si compongono
// gli ordini sulle sole righe RDA della commessa e si emettono le bozze.
function PannelloOrdiniFornitore({
  commessa, ordini, righeDaOrdinare, righeDaMettereInRda, loading, onRicarica,
}: {
  commessa: CommessaDettaglio;
  ordini: OrdineFornitoreCommessa[];
  righeDaOrdinare: number;
  righeDaMettereInRda: number;
  loading: boolean;
  onRicarica: () => void;
}) {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canOrdiniForn     = can('acquisti.ordini_fornitori', 'view');
  const canOrdiniFornEdit = can('acquisti.ordini_fornitori', 'edit');
  const canRda            = can('acquisti.rda', 'view');
  const canRichiedere     = can('acquisti.rda', 'edit') && can('commerciale.fabbisogni', 'view');
  const [emettendoId, setEmettendoId] = useState<number | null>(null);

  const nBozze   = ordini.filter(o => o.stato === 'BOZZA').length;
  const nEmessi  = ordini.filter(o => o.stato === 'EMESSO').length;

  const emetti = async (o: OrdineFornitoreCommessa) => {
    const fornitore = o.fornitore?.ragioneSociale || o.fornitoreRagioneSociale || 'il fornitore';
    if (!window.confirm(`Emettere l'ordine a ${fornitore}? Verrà assegnato il numero dal numeratore della sede.`)) return;
    setEmettendoId(o.id);
    try {
      // @api POST /api/ordini-fornitori/:id/emetti -> {"success":true,"data":{"stato":"EMESSO","numero":"string","dataEmissione":"string"}}
      const r = await apiFetch<{ success: boolean; data?: { numero: string } }>(
        `/api/ordini-fornitori/${o.id}/emetti`, { method: 'POST' }
      );
      showToast(`Ordine fornitore ${r.data?.numero ?? ''} emesso`, 'success');
      onRicarica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'emissione dell'ordine fornitore"), 'error');
    } finally {
      setEmettendoId(null);
    }
  };

  return (
    <div className="bop-riepilogo__kpi-box--table bop-riepilogo__of">
      <div className="bop-riepilogo__table-header--flex">
        <span>Ordini fornitore</span>
        <div className="bop-riepilogo__of-actions">
          {canRichiedere && (
            <button
              className="bop-btn bop-btn-secondary bop-btn-sm"
              onClick={() => navigate(`/commerciale/fabbisogni?commessaId=${commessa.id}&societaId=${commessa.societaId}`)}
              title="Apre il fabbisogno merce della commessa per generare la richiesta d'acquisto"
            >
              Richiedi acquisto
            </button>
          )}
          {canOrdiniFornEdit && (
            <button
              className="bop-btn bop-btn-primary bop-btn-sm"
              onClick={() => navigate(`/commerciale/passivo/ordini/nuovi?commessaId=${commessa.id}&societaId=${commessa.societaId}`)}
              disabled={canRda && righeDaOrdinare === 0}
              title={canRda && righeDaOrdinare === 0
                ? 'Nessuna riga RDA della commessa da ordinare: prima serve una richiesta d\'acquisto'
                : 'Compone gli ordini fornitore sulle righe RDA della commessa'}
            >
              + Ordine fornitore
            </button>
          )}
        </div>
      </div>

      <div className="bop-riepilogo__of-summary">
        {canRichiedere && (
          <span className={righeDaMettereInRda > 0 ? 'bop-riepilogo__of-chip bop-riepilogo__of-chip--warn' : 'bop-riepilogo__of-chip'}>
            Fabbisogno da richiedere: <b>{righeDaMettereInRda}</b> {righeDaMettereInRda === 1 ? 'riga' : 'righe'}
          </span>
        )}
        {canRda && (
          <span className={righeDaOrdinare > 0 ? 'bop-riepilogo__of-chip bop-riepilogo__of-chip--warn' : 'bop-riepilogo__of-chip'}>
            In RDA da ordinare: <b>{righeDaOrdinare}</b> {righeDaOrdinare === 1 ? 'riga' : 'righe'}
          </span>
        )}
        {canOrdiniForn && (
          <>
            <span className="bop-riepilogo__of-chip">Bozze: <b>{nBozze}</b></span>
            <span className="bop-riepilogo__of-chip">Emessi: <b>{nEmessi}</b></span>
          </>
        )}
      </div>

      {canOrdiniForn && (
        loading && ordini.length === 0 ? (
          <div className="bop-riepilogo__table-empty">Caricamento ordini fornitore…</div>
        ) : ordini.length === 0 ? (
          <div className="bop-riepilogo__table-empty">Nessun ordine fornitore per questa commessa</div>
        ) : (
          <table className="bop-riepilogo__table bop-table--clickable">
            <thead>
              <tr>
                <th>N.</th>
                <th>Fornitore</th>
                <th>Stato</th>
                <th>Emesso il</th>
                <th className="bop-text-right">Righe commessa</th>
                <th className="bop-text-right">Importo atteso</th>
                <th className="bop-text-right"></th>
              </tr>
            </thead>
            <tbody>
              {ordini.map(o => {
                const cfg = STATO_ORDINE_FORN[o.stato] ?? { label: o.stato, cls: 'bop-badge-neutral' };
                return (
                  <tr key={o.id} onClick={() => navigate(`/commerciale/passivo/ordini/${o.id}`)}>
                    <td className="bop-riepilogo__td-mono">{o.numero ?? 'Bozza'}</td>
                    <td>{o.fornitore?.ragioneSociale || o.fornitoreRagioneSociale || '—'}</td>
                    <td><span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span></td>
                    <td className="bop-riepilogo__td-muted">{fmtDate(o.dataEmissione)}</td>
                    <td className="bop-text-right">{o.righeCommessa}</td>
                    <td className="bop-riepilogo__td-amount" title={o.importoCommessaCompleto ? undefined : 'Alcune righe non hanno un prezzo atteso'}>
                      {fmt(Number(o.importoCommessa || 0))}{o.importoCommessaCompleto ? '' : ' *'}
                    </td>
                    <td className="bop-text-right">
                      <BopRowActions actions={[
                        {
                          icon: <RowIcon.Send />, label: 'Emetti', variant: 'primary',
                          onClick: () => emetti(o),
                          disabled: emettendoId !== null,
                          hidden: o.stato !== 'BOZZA' || !canOrdiniFornEdit,
                        },
                      ]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

function FlussRow({ label, value, warn, ok }: { label: string; value: string; warn?: boolean; ok?: boolean }) {
  const valueCls = warn ? 'bop-riepilogo__fluss-row-value--warn' : ok ? 'bop-riepilogo__fluss-row-value--ok' : 'bop-riepilogo__fluss-row-value';
  return (
    <div className="bop-riepilogo__fluss-row">
      <span className="bop-riepilogo__fluss-row-label">{label}</span>
      <span className={valueCls}>{value}</span>
    </div>
  );
}
